import { uniq } from 'mojito/utils';

// Link type (keyed in a knockout match's `matchLinks` map) that references the priced match event.
const MATCH_EVENT_LINK_TYPE = 'EVENT';
// Link type that references a priced market node directly on the knockout match.
const MATCH_MARKET_LINK_TYPE = 'MARKET';

function resolveLocalizedTranslation(translations, language) {
    if (!translations || typeof translations !== 'object') {
        return null;
    }

    return (
        (language && translations[language]) ||
        translations['-1'] ||
        translations.en ||
        Object.values(translations).find(Boolean) ||
        null
    );
}

function resolveFeedText(value, language) {
    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'object') {
        const translated = resolveLocalizedTranslation(value.translations, language);
        if (translated) {
            return String(translated);
        }

        if (value.value !== undefined && value.value !== null && value.value !== '') {
            return String(value.value);
        }
    }

    return String(value);
}

function parseKnockoutSlotScore(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function getKnockoutSlotScores(slot) {
    if (!slot) {
        return { score: null, scoreEt: null, scorePens: null };
    }

    return {
        score: parseKnockoutSlotScore(slot.score),
        scoreEt: parseKnockoutSlotScore(slot.scoreEt ?? slot.score_et),
        scorePens: parseKnockoutSlotScore(slot.scorePens ?? slot.score_pens),
    };
}

function resolveKnockoutMatchOutcome(slot1, slot2) {
    const home = getKnockoutSlotScores(slot1);
    const away = getKnockoutSlotScores(slot2);

    if (home.score === null || away.score === null) {
        return {
            hasResult: false,
            winner: null,
            homeDisplayScore: null,
            awayDisplayScore: null,
        };
    }

    const homeDisplayScore = home.score + (home.scoreEt ?? 0);
    const awayDisplayScore = away.score + (away.scoreEt ?? 0);

    if (home.scorePens !== null && away.scorePens !== null) {
        let winner = null;
        if (home.scorePens > away.scorePens) {
            winner = 'home';
        } else if (away.scorePens > home.scorePens) {
            winner = 'away';
        }

        return {
            hasResult: true,
            winner,
            homeDisplayScore,
            awayDisplayScore,
        };
    }

    let winner = null;
    if (homeDisplayScore > awayDisplayScore) {
        winner = 'home';
    } else if (awayDisplayScore > homeDisplayScore) {
        winner = 'away';
    }

    return {
        hasResult: true,
        winner,
        homeDisplayScore,
        awayDisplayScore,
    };
}

function resolveMatchEventId(match) {
    const links = match?.matchLinks;
    if (!links || typeof links !== 'object') {
        return null;
    }

    const eventId = links[MATCH_EVENT_LINK_TYPE]?.[0] ?? links.EVENT?.[0] ?? links.event?.[0];

    return eventId !== null && eventId !== undefined ? String(eventId) : null;
}

function resolveMatchMarketIds(match) {
    const links = match?.matchLinks;
    if (!links || typeof links !== 'object') {
        return [];
    }

    const marketIds = links[MATCH_MARKET_LINK_TYPE] ?? links.MARKET ?? links.market ?? [];
    return Array.isArray(marketIds) ? marketIds.map(String).filter(Boolean) : [];
}

function resolveStageMarketIds(stage) {
    const ids = new Set();
    const stageLinks = stage?.knockoutLinks;
    if (stageLinks && typeof stageLinks === 'object') {
        (stageLinks[MATCH_MARKET_LINK_TYPE] || stageLinks.MARKET || []).forEach(marketId => {
            if (marketId !== null && marketId !== undefined) {
                ids.add(String(marketId));
            }
        });
    }

    (stage?.rounds || []).forEach(round =>
        (round?.matches || []).forEach(match => {
            resolveMatchMarketIds(match).forEach(marketId => ids.add(marketId));
        })
    );

    return [...ids];
}

function countStageLinkedContent(stage) {
    let score = 0;

    (stage?.rounds || []).forEach(round =>
        (round?.matches || []).forEach(match => {
            if (resolveMatchEventId(match)) {
                score += 1;
            }
            score += resolveMatchMarketIds(match).length;
        })
    );

    return score;
}

function findEventParticipant(participants, position) {
    const target = String(position).toUpperCase();
    return (participants || []).find(
        participant => String(participant?.position || '').toUpperCase() === target
    );
}

function resolveHomeAwayEventParticipants(participants) {
    const list = participants || [];

    return {
        home: findEventParticipant(list, 'HOME') || list[0] || null,
        away: findEventParticipant(list, 'AWAY') || list[1] || null,
    };
}

function resolveLiveEventScores(event) {
    if (!event?.details?.inPlay) {
        return { home: null, away: null };
    }

    const totalScores = event.details.totalScores;
    return {
        home: parseKnockoutSlotScore(totalScores?.home ?? totalScores?.HOME),
        away: parseKnockoutSlotScore(totalScores?.away ?? totalScores?.AWAY),
    };
}

function resolveParticipantDisplayName(participant, language) {
    return (
        resolveFeedText(participant?.name, language) ||
        resolveFeedText(participant?.shortName, language) ||
        resolveFeedText(participant?.canonicalName, language)
    );
}

/**
 * Returns a compact team label for knockout bracket slots by keeping only
 * the first word of the participant name.
 *
 * @param {string|object} name - Participant full name.
 * @param {string} [language] - Active content language.
 * @returns {string} Display name for the knockout slot.
 */
function resolveKnockoutTeamDisplayName(name, language) {
    const resolvedName = resolveFeedText(name, language).trim();
    if (!resolvedName) {
        return resolvedName;
    }

    const firstSpaceIndex = resolvedName.indexOf(' ');
    if (firstSpaceIndex === -1) {
        return resolvedName;
    }

    return resolvedName.slice(0, firstSpaceIndex);
}

function participantsFromWinnerMarket(winnerMarket, language) {
    const selections = Array.isArray(winnerMarket?.selections)
        ? winnerMarket.selections.slice(0, 2)
        : [];

    if (selections.length < 2) {
        return [null, null];
    }

    return selections.map(selection => {
        const name = resolveKnockoutTeamDisplayName(selection?.name, language);
        if (!name) {
            return null;
        }

        return {
            id: String(selection.id),
            name,
            countryCode: null,
        };
    });
}

function applyParticipantFallback(participant, fallback, slot, isWinner, displayScore) {
    if (participant?.name) {
        return { ...participant, isWinner };
    }

    if (!fallback?.name) {
        return participant;
    }

    return {
        id: participant?.id || fallback.id,
        name: fallback.name,
        participantIcon: participant?.participantIcon ?? fallback.participantIcon ?? '',
        countryCode: participant?.countryCode ?? fallback.countryCode ?? null,
        score: displayScore ?? participant?.score ?? slot?.score ?? null,
        isWinner,
    };
}

function collectMatchMarkets(match, { marketsById = {}, marketsByEventId = {} } = {}) {
    const eventId = resolveMatchEventId(match);
    const eventMarkets = eventId ? marketsByEventId[String(eventId)] || [] : [];
    const linkedMarkets = resolveMatchMarketIds(match)
        .map(marketId => marketsById[String(marketId)])
        .filter(Boolean);
    const merged = [...eventMarkets];

    linkedMarkets.forEach(market => {
        const marketKey = String(market.mktId ?? market.id);
        if (!merged.some(existing => String(existing.mktId ?? existing.id) === marketKey)) {
            merged.push(market);
        }
    });

    return merged;
}

function findStoreMarketSelection(market, selectionId) {
    if (!market || selectionId === null || selectionId === undefined) {
        return null;
    }

    const id = String(selectionId);
    if (market.selectionsById?.[id]) {
        return market.selectionsById[id];
    }

    return (market.selections || []).find(selection => String(selection.id) === id) || null;
}

function enrichStageParticipantsFromStandings(stage, map, addParticipant, storeMarkets = []) {
    const marketById = new Map(
        storeMarkets.filter(Boolean).map(market => [String(market.mktId ?? market.id), market])
    );

    if (!marketById.size) {
        return;
    }

    for (const row of stage?.standings || []) {
        const rowId = String(row.id);
        if (map[rowId]) {
            continue;
        }

        for (const selection of row.selections || []) {
            const market = marketById.get(String(selection?.marketId));
            const storeSelection = findStoreMarketSelection(market, selection?.selectionId);
            const teamName = resolveFeedText(storeSelection?.name);
            if (!teamName) {
                continue;
            }

            addParticipant({
                id: rowId,
                name: teamName,
                shortName: resolveFeedText(storeSelection?.shortName || teamName),
                icon: '',
            });
            break;
        }
    }
}
/**
 * Utility helpers for bracket layout calculations and participant path lookup.
 *
 * @namespace EventBracketUtils
 * @name utils
 * @memberof Mojito.Modules.EventBracket
 */
const EventBracketsUtils = {
    /**
     * Collects the unique set of linked market ids for every event in a knockout stage.
     *
     * @param {object} event - Event object.
     * @returns {Array<string>} Array of market ids.
     */
    collectEventMarketIds(event) {
        if (!event) {
            return [];
        }

        const isDefined = id => id !== undefined && id !== null && id !== '';

        const fromMarketLines = Object.values(event.marketLines || {})
            .map(line => line?.id)
            .filter(id => isDefined(id));

        const fromMarketIds = (event.marketIds || []).filter(id => isDefined(id));

        return uniq([...fromMarketLines, ...fromMarketIds].map(String));
    },

    /**
     * Builds a map of participant id to participant object from a tournament payload.
     * Participants are aggregated from tournament-level `participants`, league stages, and
     * knockout stages so knockout match slots (which only reference participant ids) can be
     * resolve the knockout match slots (which only reference participant ids). When a standings
     * row only appears in `standings` (not `participants`), names are resolved from subscribed
     * market selections — the same enrichment used by the group standings table.
     *
     * @param {object} tournament - Tournament payload containing league stages with participants.
     * @param {string} [language] - Active content language for localized feed labels.
     * @param {Array<object>} [storeMarkets=[]] - Subscribed tournament markets.
     * @returns {Object<string, object>} Participant lookup keyed by participant id.
     */
    buildParticipantMap(tournament, language, storeMarkets = []) {
        const map = {};

        const addParticipant = participant => {
            const id = participant?.id ?? participant?.participantId;
            if (id === null || id === undefined) {
                return;
            }

            map[String(id)] = {
                ...participant,
                id: String(id),
                name: resolveFeedText(participant.name, language),
                shortName: resolveFeedText(participant.shortName || participant.name, language),
            };
        };

        (tournament?.participants || []).forEach(addParticipant);

        (tournament?.stages || []).forEach(stage => {
            (stage.participants || []).forEach(addParticipant);
            enrichStageParticipantsFromStandings(stage, map, addParticipant, storeMarkets);
        });

        (tournament?.knockoutStages || []).forEach(stage => {
            (stage.participants || []).forEach(addParticipant);
        });

        return map;
    },

    /**
     * Converts a participant icon path (e.g. `flags/JP.svg`) into a country code (e.g. `JP`).
     *
     * @param {string} [icon] - Participant icon path.
     * @returns {string|null} Country code or null when unavailable.
     */
    iconToCountryCode(icon) {
        if (!icon) {
            return null;
        }
        return icon.replace(/^flags\//, '').replace(/\.svg$/, '') || null;
    },

    /**
     * Resolves participant icon metadata from feed `icon` values (flag paths or logo URLs).
     *
     * @param {string|object} [icon] - Participant icon from tournament or event payload.
     * @param {string} [language] - Active content language for localized feed labels.
     * @returns {{participantIcon: string, countryCode: string|null}} Icon URL and optional country code.
     */
    resolveParticipantIconFields(icon, language) {
        const resolvedIcon = resolveFeedText(icon, language);
        const trimmedIcon = String(resolvedIcon || '').trim();
        const participantIcon = /^https?:\/\//i.test(trimmedIcon) ? trimmedIcon : '';

        return {
            participantIcon,
            countryCode: participantIcon ? null : EventBracketsUtils.iconToCountryCode(trimmedIcon),
        };
    },

    /**
     * Checks whether a knockout stage has at least one round with matches to render.
     *
     * @param {object} stage - Knockout stage.
     * @returns {boolean} True when the stage contains renderable matches.
     */
    hasRenderableMatches(stage) {
        return (stage?.rounds || []).some(round => (round?.matches || []).length > 0);
    },

    /**
     * Selects the knockout stage to render for a tournament.
     *
     * A tournament can expose several knockout stages (for example, draft/suspended test brackets
     * alongside the live one). We render a single bracket, so we prefer a displayed, `ACTIVE` stage
     * that actually has matches, then fall back to any displayed stage with matches, and finally to
     * the first stage. This avoids rendering an empty/suspended stage while the real bracket exists.
     *
     * @param {object} tournament - Tournament payload containing knockout stages.
     * @returns {object|undefined} The knockout stage to render.
     */
    selectKnockoutStage(tournament) {
        const stages = (tournament?.knockoutStages || []).filter(
            stage => stage?.displayed !== false
        );
        const stagesWithMatches = stages.filter(EventBracketsUtils.hasRenderableMatches);

        if (!stagesWithMatches.length) {
            return stages[0] || (tournament?.knockoutStages || [])[0];
        }

        const rankedStages = stagesWithMatches.slice().sort((left, right) => {
            const activeScore =
                Number(right.status === 'ACTIVE') - Number(left.status === 'ACTIVE');
            if (activeScore !== 0) {
                return activeScore;
            }

            const linkScore = countStageLinkedContent(right) - countStageLinkedContent(left);
            if (linkScore !== 0) {
                return linkScore;
            }

            return (right.participantCount || 0) - (left.participantCount || 0);
        });

        return rankedStages[0];
    },

    /**
     * Returns the id of the priced match event linked to a knockout match (via `matchLinks.EVENT`).
     *
     * @param {object} match - Knockout match.
     * @returns {string|null} Linked event id or null when the match has no event link.
     */
    getMatchEventId(match) {
        return resolveMatchEventId(match);
    },

    /**
     * Collects the unique set of linked event ids for every match in a knockout stage. These are
     * the events that must be subscribed to so the bracket cards can render participants and prices.
     *
     * @param {object} stage - Knockout stage.
     * @returns {Array<string>} Unique linked event ids.
     */
    collectStageEventIds(stage) {
        const ids = new Set();
        (stage?.rounds || []).forEach(round =>
            (round?.matches || []).forEach(match => {
                const eventId = EventBracketsUtils.getMatchEventId(match);
                if (eventId) {
                    ids.add(eventId);
                }
            })
        );
        return [...ids];
    },

    /**
     * Returns market ids linked directly on a knockout match (`matchLinks.MARKET`).
     *
     * @param {object} match - Knockout match.
     * @returns {Array<string>} Linked market ids.
     */
    getMatchMarketIds(match) {
        return resolveMatchMarketIds(match);
    },

    /**
     * Collects market ids linked from knockout matches and stage-level `knockoutLinks`.
     * Standings tables resolve markets via `standings[].selections`; knockout matches can
     * reference markets the same way through `matchLinks.MARKET`.
     *
     * @param {object} stage - Knockout stage.
     * @returns {Array<string>} Unique linked market ids.
     */
    collectStageMarketIds(stage) {
        return resolveStageMarketIds(stage);
    },

    /**
     * Picks the two markets rendered on a bracket card from a match event's markets: the 2-way
     * "to win" market (mapped onto the participant slots) and the 3-way 1 X 2 market (rendered in
     * the card footer). Falls back to selection-count heuristics when the market types differ.
     *
     * @param {Array<object>} markets - Markets belonging to the linked match event.
     * @param {{winnerMarketTypes: Array<string>, matchResultMarketTypes: Array<string>}} [marketTypes={}] - Winner/footer market type config.
     * @returns {{winnerMarket: (object|null), bottomMarket: (object|null)}} Resolved bracket markets.
     */
    selectMatchMarkets(markets, marketTypes = {}) {
        const { matchResultMarketTypes = [], winnerMarketTypes = [] } = marketTypes;
        const list = Array.isArray(markets) ? markets.filter(Boolean) : [];
        const winnerMarket =
            list.find(market => winnerMarketTypes.includes(market?.type)) ||
            list.find(market => (market?.selections || []).length === 2) ||
            null;
        const bottomMarket =
            list.find(market => matchResultMarketTypes.includes(market?.type)) ||
            list.find(
                market => market !== winnerMarket && (market?.selections || []).length === 3
            ) ||
            null;
        return { winnerMarket, bottomMarket };
    },

    /**
     * Transforms a tournament payload into the bracket data shape used by `EventBracket`.
     *
     * The knockout topology (rounds/matches) is embedded in the tournament (`knockoutStages`).
     * UI consumers subscribe once via `useTournament` — the separate `brackets` collection is not
     * populated and must not be used for knockout rendering.
     * Participant details are resolved from the league stages' participant lists (via the match
     * slots), and prices are resolved from the subscribed match events referenced by
     * `matchLinks.EVENT`. When a match has no populated slot participant, the linked event's
     * participants are used as a fallback so the card still shows a team name rather than `TBD`.
     *
     * @param {object} tournament - Tournament payload containing knockout stages.
     * @param {object} [subscriptions] - Subscribed content used to enrich bracket nodes.
     * @param {Object<string, object>} [subscriptions.eventsById] - Linked match events keyed by id.
     * @param {Object<string, object>} [subscriptions.marketsById] - Subscribed markets keyed by market id.
     * @param {Object<string, Array<object>>} [subscriptions.marketsByEventId] - Subscribed markets keyed by event id.
     * @param {Array<object>} [subscriptions.storeMarkets] - Subscribed tournament outright markets.
     * @param {string} [subscriptions.language] - Active content language for localized feed labels.
     * @param {object} [subscriptions.marketTypes] - Market types. Has to have structure as defined in EventBracketMarketOptions.
     * @returns {{name: string, rounds: Array<object>, sportId: string|undefined}} Bracket-ready data.
     */
    transformKnockoutData(
        tournament,
        {
            eventsById = {},
            marketsById = {},
            marketsByEventId = {},
            storeMarkets = [],
            language,
            marketTypes,
        } = {}
    ) {
        const participantMap = EventBracketsUtils.buildParticipantMap(
            tournament,
            language,
            storeMarkets
        );
        const sportId = tournament?.sportId;
        const knockoutStage = EventBracketsUtils.selectKnockoutStage(tournament);

        const mapSlotToParticipant = (slot, eventParticipant, isWinner, displayScore) => {
            const hasSlotParticipantId =
                slot?.participantId !== null &&
                slot?.participantId !== undefined &&
                slot.participantId !== 0;

            const tournamentParticipant =
                hasSlotParticipantId && participantMap[String(slot.participantId)];

            if (tournamentParticipant) {
                const { participantIcon, countryCode } =
                    EventBracketsUtils.resolveParticipantIconFields(
                        tournamentParticipant.icon,
                        language
                    );

                return {
                    id: String(tournamentParticipant.id),
                    name: resolveKnockoutTeamDisplayName(tournamentParticipant.name, language),
                    participantIcon,
                    countryCode,
                    score: displayScore ?? slot?.score ?? null,
                    isWinner,
                };
            }

            if (eventParticipant) {
                const name = resolveKnockoutTeamDisplayName(
                    resolveParticipantDisplayName(eventParticipant, language),
                    language
                );
                if (!name) {
                    return null;
                }

                const { participantIcon, countryCode } =
                    EventBracketsUtils.resolveParticipantIconFields(
                        eventParticipant.icon,
                        language
                    );

                return {
                    id: String(eventParticipant.id),
                    name,
                    participantIcon,
                    countryCode,
                    score: displayScore ?? slot?.score ?? null,
                    isWinner,
                };
            }

            return null;
        };

        const rounds = [...(knockoutStage?.rounds || [])]
            .sort((left, right) => (left?.roundNumber || 0) - (right?.roundNumber || 0))
            .map(round => ({
                name: resolveFeedText(round.name, language),
                nodes: [...(round.matches || [])]
                    .sort((left, right) => (left?.matchNumber || 0) - (right?.matchNumber || 0))
                    .map(match => {
                        const eventId = resolveMatchEventId(match);
                        const linkedEvent = (eventId && eventsById[eventId]) || null;
                        const { home: homeEventParticipant, away: awayEventParticipant } =
                            resolveHomeAwayEventParticipants(linkedEvent?.participants);
                        const matchMarkets = collectMatchMarkets(match, {
                            marketsById,
                            marketsByEventId,
                        });
                        const { winnerMarket } = EventBracketsUtils.selectMatchMarkets(
                            matchMarkets,
                            marketTypes
                        );
                        const [homeFromMarket, awayFromMarket] = participantsFromWinnerMarket(
                            winnerMarket,
                            language
                        );

                        const { hasResult, winner, homeDisplayScore, awayDisplayScore } =
                            resolveKnockoutMatchOutcome(match.slot1, match.slot2);
                        const isLive = Boolean(linkedEvent?.details?.inPlay);
                        const liveScores = resolveLiveEventScores(linkedEvent);
                        const homeScore = isLive
                            ? (liveScores.home ?? homeDisplayScore)
                            : homeDisplayScore;
                        const awayScore = isLive
                            ? (liveScores.away ?? awayDisplayScore)
                            : awayDisplayScore;
                        const homeParticipant = applyParticipantFallback(
                            mapSlotToParticipant(
                                match.slot1,
                                homeEventParticipant,
                                winner === 'home',
                                homeScore
                            ),
                            homeFromMarket,
                            match.slot1,
                            winner === 'home',
                            homeScore
                        );
                        const awayParticipant = applyParticipantFallback(
                            mapSlotToParticipant(
                                match.slot2,
                                awayEventParticipant,
                                winner === 'away',
                                awayScore
                            ),
                            awayFromMarket,
                            match.slot2,
                            winner === 'away',
                            awayScore
                        );
                        let status = hasResult ? 'finished' : 'scheduled';
                        if (isLive) {
                            status = 'live';
                        }

                        return {
                            id: String(match.id),
                            label: '',
                            status,
                            slots: [
                                { participant: homeParticipant },
                                { participant: awayParticipant },
                            ],
                            event: linkedEvent,
                            linkedMarketIds: resolveMatchMarketIds(match),
                        };
                    }),
            }));

        return {
            name: resolveFeedText(tournament?.name, language),
            rounds,
            sportId,
        };
    },

    /**
     * Returns the vertical spacing multiplier for nodes in a given round.
     *
     * The multiplier grows by powers of two as the bracket advances,
     * so later-round cards can be vertically centered across the span
     * occupied by the previous round's matches.
     *
     * @param {number} roundIndex - Zero-based index of the current round.
     * @returns {number} Round multiplier used for layout calculations.
     */
    getNodeMultiplier(roundIndex) {
        return Math.pow(2, roundIndex);
    },

    /**
     * Calculates the top offset for a node within a bracket column.
     *
     * The node is centered within the vertical span represented by its
     * round multiplier so connectors line up correctly between rounds.
     *
     * @param {number} nodeIndex - Zero-based node position inside the round.
     * @param {number} multiplier - Round multiplier returned by `getNodeMultiplier`.
     * @param {number} cardHeight - Full rendered card height in pixels.
     * @param {number} rowGap - Vertical gap between bracket blocks in pixels.
     * @returns {number} Top position in pixels for the node.
     */
    getNodeTop(nodeIndex, multiplier, cardHeight, rowGap) {
        const slotHeight = cardHeight + rowGap;
        const spanHeight = multiplier * slotHeight - rowGap;
        const centerOffset = (spanHeight - cardHeight) / 2;
        return nodeIndex * multiplier * slotHeight + centerOffset;
    },

    /**
     * Calculates the full height required for a bracket column.
     *
     * @param {number} nodeCount - Number of nodes rendered in the column.
     * @param {number} multiplier - Round multiplier returned by `getNodeMultiplier`.
     * @param {number} blockHeight - Height of a single node block including spacing.
     * @param {number} rowGap - Vertical gap between bracket blocks in pixels.
     * @returns {number} Total column height in pixels.
     */
    getColumnHeight(nodeCount, multiplier, blockHeight, rowGap) {
        return Math.max(0, nodeCount * multiplier * (blockHeight - rowGap));
    },

    /**
     * Finds all node ids that belong to a participant's path through the bracket.
     *
     * The method scans every round and node, checks each node slot for a matching
     * participant id, and collects the ids of nodes where the participant appears.
     *
     * @param {Array<object>} rounds - Bracket rounds containing node collections.
     * @param {string} participantId - Participant id to search for.
     * @returns {Set<string>} Set of node ids that contain the participant.
     */
    findParticipantPathNodeIds(rounds, participantId) {
        const ids = new Set();
        rounds.forEach(round => {
            round.nodes.forEach(node => {
                const inSlot = node.slots.some(
                    s => s.participant && s.participant.id === participantId
                );
                if (inSlot) ids.add(node.id);
            });
        });
        return ids;
    },

    getRoundLayoutMetrics(round, roundIndex, activeIndex, nodeConfig) {
        const multiplier = this.getEffectiveMultiplier(roundIndex, activeIndex);
        const cardHeight = nodeConfig.cardFullHeight;
        const rowGap = nodeConfig.rowGap;
        const nodeCount = round?.nodes?.length || 0;
        const lastTop = EventBracketsUtils.getNodeTop(
            round.nodes.length - 1,
            multiplier,
            cardHeight,
            rowGap
        );
        const colHeight = nodeCount > 0 ? lastTop + cardHeight : 0;
        return { multiplier, colHeight };
    },

    getEffectiveMultiplier(roundIndex, activeIndex) {
        const relativeIndex = roundIndex - activeIndex;
        // selected and future columns: 1, 2, 4, 8...
        if (relativeIndex >= 0) {
            return Math.pow(2, relativeIndex);
        }
        // previous columns: keep compact
        return Math.max(1, Math.pow(2, relativeIndex));
    },

    resolveKnockoutTeamDisplayName,
};

export default EventBracketsUtils;
